<template>
    <div>
        <Accordion>
            <template v-slot:title>{{ informationText.manualCalculationMaintenanceTherapy.title }}</template>
            <template v-slot:content>
                <p>Suggested maintenance fluid for majority of patients is 0.18% sodium chloride/4% glucose with potassium
                    chloride (40mmol in 1000ml)</p>
                <p>This will meet the patient requirements of water, sodium, potassium and glucose. <strong>Excessive
                        volumes of this fluid may cause hyponatraemia</strong></p>
                <ul>
                    <li>1. Obtain patients weight in kg</li>
                    <li>2. Maintenance fluid requirement is 30ml/kg/24hr (reduce to 20 ml/kg/24hr in frailty)</li>
                    <li>3. Use table below to choose volume and rate:</li>
                </ul>
                <div class="table-responsive">
                    <table class="table table-bordered iv-fluids-table-striped" role="presentation">
                        <thead>
                            <tr aria-colspan="">
                                <th><strong>Weight (kg)</strong></th>
                                <th><strong>Fluid (ml/day)</strong></th>
                                <th><strong>Fluid (ml/hr)</strong></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>35-44</td>
                                <td>1,200</td>
                                <td>50</td>
                            </tr>
                            <tr>
                                <td>45-54</td>
                                <td>1,500</td>
                                <td>65</td>
                            </tr>
                            <tr>
                                <td>55-64</td>
                                <td>1,800</td>
                                <td>75</td>
                            </tr>
                            <tr>
                                <td>65-74</td>
                                <td>2,100</td>
                                <td>85</td>
                            </tr>
                            <tr>
                                <td>>75</td>
                                <td>2,400</td>
                                <td>100(Max)</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <ul>
                    <li>4. Review daily U&Es, additional electrolytes and Hb.</li>
                </ul>
            </template>
        </Accordion>

        <Accordion>
            <template v-slot:title>{{ informationText.electrolyteRequirementsForMaintenance.title }}</template>
            <template v-slot:content>
                <div class="table-responsive">
                    <table class="table table-bordered iv-fluids-table-striped" role="presentation">
                        <thead>
                            <tr aria-colspan="">
                                <th class="table-header-electrolyte"><strong>Electrolyte</strong></th>
                                <th><strong>Requirement</strong></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Sodium</td>
                                <td>1 mmol/kg/24 hrs</td>
                            </tr>
                            <tr>
                                <td>Potassium</td>
                                <td>1 mmol/kg/24hrs (give 40mmol potassium in 1L maintenance fluid)</td>
                            </tr>
                            <tr>
                                <td>Glucose</td>
                                <td>1g/kg/24 hrs to minimise starvation ketosis (1L 0.18%NaCl 4% glucose contains 40g 
                                    glucose)</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <p>EXCEPT:</p>
                <p><strong>High potassium</strong> (>5mmol/l)</p>
                <p>Do not give potassium containing fluids → Give 0.18% sodium chloride/4% glucose (Seek senior advice in
                    renal failure)</p>
                <p><strong>Low sodium</strong> (&le;=132 mmol/l)</p>
                <p>→ Give Hartmanns or other balanced crystalloid for maintenance. Monitor U+Es regularly and consult senior
                </p>
            </template>
        </Accordion>

        <Accordion>
            <template v-slot:title>{{ informationText.manualCalculationReplacementTherapy.title }}</template>
            <template v-slot:content>
                <p>Add up all the losses over the previous 24 hours from fluid balance chart and give this volume as
                    Hartmanns , other balanced crystalloid, or 0.9% sodium chloride (for vomiting or nasogastric fluid
                    loss.)</p>
                <p>Patients require daily reassessment or more frequent if they have high losses.</p>
                <p><strong>Electrolyte and other contents of IV fluids (per litre; unless stated, units in mmol/l)</strong>
                </p>
                <div class="table-responsive">
                    <table class="table table-bordered iv-fluids-table-striped" role="presentation">
                        <thead>
                            <tr aria-colspan="">
                                <th><strong>Fluid</strong></th>
                                <th><strong>Sodium</strong></th>
                                <th><strong>Potassium</strong></th>
                                <th><strong>Chloride</strong></th>
                                <th><strong>Magnesium</strong></th>
                                <th><strong>Calcium</strong></th>
                                <th><strong>Other</strong></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>0.18% sodium chloride 4% glucose</td>
                                <td>30</td>
                                <td></td>
                                <td>30</td>
                                <td></td>
                                <td></td>
                                <td>40g glucose</td>
                            </tr>
                            <tr>
                                <td>Hartmanns</td>
                                <td>131</td>
                                <td>5</td>
                                <td>112</td>
                                <td></td>
                                <td>2</td>
                                <td>28 bicarbonate</td>
                            </tr>
                            <tr>
                                <td>0.9% sodium chloride</td>
                                <td>154</td>
                                <td></td>
                                <td>154</td>
                                <td></td>
                                <td></td>
                                <td></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <p><strong>Sodium replacement</strong></p>
                <p>Hyponatraemia is common: in the absence of large gastrointestinal losses the causes are almost always too
                    much fluid. Also consider SIADH, or chronic diuretic use.</p>
                <p><strong>Refer to local hyponatraemia guideline</strong></p>
                <p><strong>Potassium replacement</strong></p>
                <p>For IV replacement on general adult wards: 20mmol of potassium chloride diluted in 500ml 0.9% sodium
                    chloride and given @100ml/hr.</p>
                <p>The maximum rate of potassium administration is 10mmol/hr in a ward setting. Patients receiving this do
                    not require cardiac monitoring.</p>
                <p>40mmol of KCl may be given in 500ml 0.9% NaCl at no more than 100ml/h. This must be agreed by a senior
                    doctor due to the risk of tissue damage.</p>
                <p><strong>However, extravasation of potassium is harmful; ensure the cannula is working well and is checked
                    hourly. Do not use fragile veins or veins in the feet.</strong></p>
            </template>
        </Accordion>
    </div>
</template>

<script>
import Accordion from '../controls/accordion.vue'

export default {
    components: {
        Accordion,
    },
    props: ["informationText"] 
}
</script>
